import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Zap, Wrench, ShieldCheck, ArrowRight, Search, Truck, CreditCard, BadgeCheck } from "lucide-react";

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [promo, setPromo] = useState(null);
  const [ticket, setTicket] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Troti-X — Trotinete electrice Timișoara | Magazin & Service";
    api.get("/shop/products").then((r) => {
      const sorted = [...r.data].sort((a, b) => (b.featured === true) - (a.featured === true));
      setProducts(sorted.slice(0, 4));
    }).catch(() => {});
    api.get("/shop/promo/active").then((r) => { if (r.data.active) setPromo(r.data); }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* BANNER PROMO */}
      {promo && (
        <Link to="/magazin" data-testid="promo-banner" className="block bg-[#00E5FF] text-black overflow-hidden group">
          <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-center gap-3 text-center flex-wrap">
            <Zap className="w-4 h-4 flex-shrink-0" fill="currentColor" />
            <span className="text-xs sm:text-sm font-bold uppercase tracking-wider">
              Oferta momentului: <span className="bg-black text-[#00E5FF] px-2 py-0.5 font-mono">{promo.code}</span>
              {" "}= {promo.type === "percent" ? `-${promo.value}% reducere` : `-${promo.value} RON reducere`} + livrare GRATUITĂ
            </span>
            <span className="text-xs underline font-semibold group-hover:no-underline hidden sm:inline">Profită acum →</span>
          </div>
        </Link>
      )}

      {/* HERO */}
      <section className="relative overflow-hidden tx-grid-bg">
        <div className="absolute inset-0">
          <img src="https://static.prod-images.emergentagent.com/jobs/833a4d93-2b4c-4664-8200-fc7fb33fcb24/images/a1faa6654958db13cc5a73c539d4811f10fad8cfcd53a763188f4f6deb3454c1.jpeg"
            alt="" className="w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-36">
          <div className="inline-flex items-center gap-2 border border-[#00E5FF]/40 text-[#00E5FF] text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 mb-8">
            <Zap className="w-3.5 h-3.5" fill="currentColor" /> Magazin + Service · Timisoara
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[1.05] max-w-3xl tx-flicker">
            Energie pura<br />
            pe <span className="text-[#00E5FF] tx-text-glow">doua roti</span>
          </h1>
          <p className="text-neutral-400 text-base max-w-xl mt-6 leading-relaxed">
            Suntem atelierul din Rebreanu 104A — vindem trotinete pe care le-am testat noi înșine,
            avem piese pe stoc și le reparăm tot noi dacă e nevoie. Comanzi online, plătești cu cardul
            sau ramburs, iar dacă ai o problemă ne suni direct, nu vorbești cu un robot.
          </p>
          <div className="flex flex-wrap gap-4 mt-10">
            <Link to="/magazin" data-testid="hero-shop-btn"
              className="group inline-flex items-center gap-3 bg-[#00E5FF] hover:bg-[#5CEEFF] text-black font-bold px-8 py-4 uppercase tracking-wider text-sm transition-colors tx-cyan-glow">
              Vezi trotinetele <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/programare" data-testid="hero-service-btn"
              className="inline-flex items-center gap-3 border border-[#00E5FF]/40 hover:border-[#00E5FF] text-white font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-colors">
              <Wrench className="w-4 h-4 text-[#00E5FF]" /> Programare service
            </Link>
          </div>
        </div>
      </section>

      {/* BENEFICII */}
      <section className="border-y border-[#00E5FF]/10 bg-[#070707]">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#00E5FF]/10">
          {[
            { icon: Truck, t: "Livrare GRATUITĂ", d: "Transport gratuit in toata tara, cu AWB" },
            { icon: CreditCard, t: "Card sau ramburs", d: "Platesti cum iti convine" },
            { icon: Wrench, t: "Service propriu", d: "Reparatii in atelierul nostru" },
            { icon: BadgeCheck, t: "Garantie asigurata", d: "Partener trotinete-ieftine.ro" },
          ].map((b, i) => (
            <div key={i} className="p-6 lg:p-8">
              <b.icon className="w-6 h-6 text-[#00E5FF] mb-3" />
              <div className="font-bold text-sm">{b.t}</div>
              <div className="text-xs text-neutral-500 mt-1">{b.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUSE */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-bold mb-2">// Magazin</div>
            <h2 className="font-display text-lg uppercase">Trotinete in stoc</h2>
          </div>
          <Link to="/magazin" data-testid="see-all-products-link" className="text-xs uppercase tracking-widest text-[#00E5FF] hover:text-white transition-colors flex items-center gap-2">
            Vezi toate <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        {products.length === 0 ? (
          <div className="border border-dashed border-[#00E5FF]/20 p-16 text-center text-neutral-500">
            <Zap className="w-10 h-10 mx-auto mb-4 text-[#00E5FF]/30" />
            Produsele sosesc in curand. Revino repede!
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* SERVICE BAND */}
      <section className="relative overflow-hidden border-y border-[#00E5FF]/10">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <img src="https://customer-assets-v7afamib.emergentagent.net/job_kukirin-tech-service/artifacts/l0jnbt1w_WhatsApp%20Image%202026-08-29%20at%2018.59.03.jpeg"
              alt="Magazinul si atelierul Troti-X din Timisoara" className="w-full aspect-[4/3] object-cover border border-[#00E5FF]/20" />
            <div className="absolute -bottom-4 -right-4 bg-[#00E5FF] text-black px-5 py-3 font-display text-sm uppercase">
              Atelier propriu
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-bold mb-2">// Service</div>
            <h2 className="font-display text-lg uppercase leading-snug">Reparatii rapide, statusuri live</h2>
            <p className="text-neutral-400 text-sm mt-4 leading-relaxed">
              Iti reparam trotineta indiferent de unde ai cumparat-o. Primesti SMS cand e gata si
              poti verifica oricand statusul reparatiei cu numarul de tichet.
            </p>
            <form data-testid="status-check-form" className="mt-8 flex gap-0 max-w-md"
              onSubmit={(e) => { e.preventDefault(); if (ticket.trim()) navigate(`/status/${ticket.trim()}`); }}>
              <input data-testid="status-ticket-input" value={ticket} onChange={(e) => setTicket(e.target.value)}
                placeholder="Numar tichet (ex: A1B2C3)"
                className="flex-1 bg-[#0B0B0B] border border-[#00E5FF]/30 focus:border-[#00E5FF] px-4 py-3.5 text-sm font-mono uppercase placeholder:normal-case placeholder:font-body" />
              <button data-testid="status-check-btn" type="submit" className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black px-5 transition-colors">
                <Search className="w-4 h-4" />
              </button>
            </form>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/programare" data-testid="service-booking-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#00E5FF] hover:text-white transition-colors">
                <Wrench className="w-4 h-4" /> Programeaza o reparatie
              </Link>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500">
                <ShieldCheck className="w-4 h-4 text-[#00E5FF]" /> Garantii trotinete-ieftine.ro
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="relative bg-[#00E5FF] text-black p-10 lg:p-16 overflow-hidden">
          <Zap className="absolute -right-6 -top-6 w-48 h-48 text-black/10" fill="currentColor" />
          <div className="font-display text-2xl lg:text-4xl uppercase leading-tight max-w-2xl">
            Gata de viteza? Alege-ti trotineta acum.
          </div>
          <Link to="/magazin" data-testid="cta-shop-btn"
            className="inline-flex items-center gap-3 bg-black text-white font-bold px-8 py-4 uppercase tracking-wider text-sm mt-8 hover:bg-neutral-800 transition-colors">
            Intra in magazin <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
